import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import Loader from "../../components/loader";
import api from "../../utils/api";
import { errorMessage } from "../../utils/utils";
import moment from "moment";
import { IoIosCalendar, } from "react-icons/io";
import { MdOutlineHistory } from "react-icons/md";
import { Card, EmptyState, ErrorState, Loading, Page, Pill, TableWrap } from "../../components/ui";

export default function Logs() {

    const [pageLoading, setPageLoading] = useState(true);
    const [itemsLoading, setItemsLoading] = useState(true);
    // const [error, setError] = useState('');
    const [itemsError, setItemsError] = useState('');
    const [items, setItems] = useState<any[]>([]);
    const [values, setValues] = useState<any[]>([]);


    useEffect(() => {
        const token = window.localStorage.token;

        if (!token) {
            window.location.replace('/signin');
            return;
        }

        setPageLoading(false);
        getLogs();
    }, []);


    async function getLogs(name?: string) {
        setItems([]);
        setItemsLoading(true);
        setItemsError('');
        try {
            const res = await api.get(`/remote/logs/${name ?? ''}`);
            const json = await res.json();
            if (res.ok) {
                if (name) {
                    setItems(json);
                } else {
                    setItems(json.logs);
                    setValues(json.values);
                }
            } else {
                setItemsError(json.error);
            }
        } catch (err) {
            setItemsError(errorMessage(err));
        }
        setItemsLoading(false);
    }

    if (pageLoading) {
        return (<div className='w-screen h-screen text-center flex flex-col bg-white justify-center'><Loader /></div>)
    }

    function Filter() {
        return <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label htmlFor="log-filter" className="shrink-0 text-xs font-medium text-gray-500">Filter by account</label>
            <select
                id="log-filter"
                className="min-h-[38px] w-full sm:w-[240px] rounded-lg bg-white px-2 text-sm outline-none ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                    getLogs(e.target.value);
                }}>
                <option value="">All accounts</option>
                {values?.map((item: any, index: number) => {
                    return <option key={`${index}-filter`} value={item.name}>{item.label ?? item.name}</option>
                })}
            </select>
        </div>;
    }

    return (
        <Layout nav='logs' title='Connection Logs'>
            <Page wide>

                <Card className="mb-4">{Filter()}</Card>

                {itemsLoading && <Loading label="Loading logs..." />}
                {(!itemsLoading && itemsError) && <ErrorState error={itemsError} />}
                {(!itemsLoading && !itemsError && items.length === 0) &&
                    <EmptyState icon={MdOutlineHistory} title="No connection logs" message="Activity from your remote accounts will show up here." />}

                {(!itemsLoading && !itemsError && items.length > 0) && <>
                    <div className="md:hidden"><Medium items={items} /></div>
                    <div className="hidden md:block"><Large items={items} /></div>
                </>}

            </Page>
        </Layout>
    )
}

function statusTone(status: string) {
    return status === 'up' ? 'green' : status === 'down' ? 'red' : 'gray';
}

function Medium({ items }: any) {
    return (
        <ul className="flex flex-col gap-3">{items?.map((item: any, index: number) => {

            const date = item.date_created ? moment(item.date_created).startOf('seconds').fromNow() : 'n/a';

            return <li key={`${index}-item-medium`}>
                <Card className="flex flex-row items-start gap-3">

                    <div className="grow min-w-0 flex flex-col gap-0.5">
                        <div className="truncate font-semibold text-gray-900">{item.label}</div>
                        <div className="truncate text-xs text-gray-500">{item.name}</div>
                        <div className="mt-1 flex flex-row items-center gap-1.5 text-[11px] text-gray-500">
                            <IoIosCalendar className="shrink-0" /><span>{date}</span>
                        </div>
                        <div className="text-[11px] text-gray-400">{moment(item.date_created).format('LLL')}</div>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-1">
                        <Pill tone={statusTone(item.status)}>{item.status}</Pill>
                        {item.reason && <div className="max-w-[110px] break-words text-right text-[11px] text-gray-500">{item.reason}</div>}
                    </div>

                </Card>
            </li>
        })}</ul>
    );
}


function Large({ items }: any) {
    return (
        <TableWrap>
            <table className="w-full min-w-[720px] divide-y divide-gray-200 text-left">
                <thead className="bg-slate-50/80 text-[11px] uppercase tracking-[0.08em] text-slate-500">
                    <tr>
                        <th className="px-4 py-3 font-semibold w-[170px]">Date</th>
                        <th className="px-4 py-3 font-semibold w-[130px]">Name</th>
                        <th className="px-4 py-3 font-semibold">Label</th>
                        <th className="px-4 py-3 font-semibold w-[140px]">Reason</th>
                        <th className="px-4 py-3 font-semibold w-[110px]">Status</th>
                    </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                    {items.map((item: any, index: number) => {

                        const date = item.date_created ? moment(item.date_created).startOf('seconds').fromNow() : 'n/a';

                        return <tr key={`${index}-item`} className="transition hover:bg-blue-50/50">

                            <td className="px-4 py-3 text-sm text-gray-600">
                                <div className="group relative flex items-center gap-1.5">
                                    <IoIosCalendar className="shrink-0 text-gray-400" />
                                    <span className="whitespace-nowrap">{date}</span>
                                    <span className="pointer-events-none absolute left-0 top-6 z-10 scale-0 whitespace-nowrap rounded bg-gray-800 p-2 text-xs text-white transition-all group-hover:scale-100">
                                        {moment(item.date_created).format('LLL')}
                                    </span>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.name}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.label}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.reason}</td>
                            <td className="px-4 py-3"><Pill tone={statusTone(item.status)}>{item.status}</Pill></td>
                        </tr>
                    })}
                </tbody>

            </table>
        </TableWrap>
    )
}
